import {
  useAddItemsToMonitoringAssortment,
  useCreateMonitoringAssortment,
  useDeleteItemsFromMonitoringAssortment,
  useDeleteMonitoringAssortment,
  usePatchTitleOrItemsOfMonitoringAssortment,
  useValidateImportedItems,
} from '@retail/monitoring-assortment/data-access';
import { useQueryClient } from '@tanstack/react-query';
import { ItemsCodeLists } from '@retail/monitoring-assortment/types';
import { CreateAssortment } from '@retail/monitoring-assortment/types';

export interface ItemsMgAssortmentArgs {
  assortmentId: number;
  mgItemNumbers: number[];
  finfoNumbers?: number[];
  gtinCodes?: number[];
}

export interface ReplaceMgAssortmentArgs {
  assortmentId: number;
  title: string;
}

export function useMonitoringAssortmentActions() {
  const queryClient = useQueryClient();

  const { mutateAsync: mutateCreateAsync, isPending: isLoadingCreate } =
    useCreateMonitoringAssortment();

  const { mutateAsync: mutateDeleteAsync, isPending: isLoadingDelete } =
    useDeleteMonitoringAssortment();

  const { mutateAsync: mutatePostAsync, isPending: isLoadingPost } =
    useAddItemsToMonitoringAssortment();

  const { mutateAsync: mutateDeleteItemsAsync, isPending: isLoadingDeleteItems } =
    useDeleteItemsFromMonitoringAssortment();

  const { mutateAsync: mutateChangeTitleAsync, isPending: isLoadingChangeTitle } =
    usePatchTitleOrItemsOfMonitoringAssortment();

  const { mutateAsync: mutateValidateItemsAsync } = useValidateImportedItems();

  const handleCreateMonitoringAssortment = async (
    { title, itemCodes }: CreateAssortment,
    orgUnitId: number
  ) => {
    const assortment = await mutateCreateAsync({
      body: { title, itemCodes: itemCodes ?? {}, orgUnitId },
    });

    await queryClient.invalidateQueries({ queryKey: ['monitoring-assortment'] });
    queryClient.invalidateQueries({ queryKey: ['assortments-for-mg-item'] });
    queryClient.invalidateQueries({ queryKey: ['orgUnit-assortments'] });
    return assortment;
  };

  const handleDeleteMgAssortment = async (assortmentId: number) => {
    await mutateDeleteAsync({ urlVariables: assortmentId });
    queryClient.invalidateQueries({ queryKey: ['monitoring-assortment'] });
  };

  const addItemsToAssortment = async ({
    mgItemNumbers,
    finfoNumbers,
    gtinCodes,
    assortmentId,
  }: ItemsMgAssortmentArgs) => {
    const updatedAssortment = await mutatePostAsync({
      body: {
        mgItemNumbers,
        finfoNumbers,
        gtinCodes,
      },
      urlVariables: assortmentId,
    });
    await queryClient.invalidateQueries({ queryKey: ['monitoring-assortment'] });
    queryClient.invalidateQueries({ queryKey: ['assortments-for-mg-item'] });

    return updatedAssortment;
  };

  const deleteItemsFromAssortment = async ({
    mgItemNumbers,
    assortmentId,
  }: ItemsMgAssortmentArgs) => {
    const updatedAssortment = await mutateDeleteItemsAsync({
      body: {
        mgItemNumbers,
      },
      urlVariables: assortmentId,
    });
    await queryClient.invalidateQueries({ queryKey: ['monitoring-assortment'] });
    queryClient.invalidateQueries({ queryKey: ['assortments-for-mg-item'] });

    return updatedAssortment;
  };

  const handlePatchTitleOrItemsOfMgAssortment = async ({
    title,
    assortmentId,
  }: ReplaceMgAssortmentArgs) => {
    await mutateChangeTitleAsync({
      body: { title },
      urlVariables: assortmentId,
    });
    queryClient.invalidateQueries({ queryKey: ['monitoring-assortment'] });
    queryClient.invalidateQueries({ queryKey: ['orgUnit-assortments'] });
  };

  const handleValidateItemsOnImportedAssortment = async (itemCodes: ItemsCodeLists) => {
    return await mutateValidateItemsAsync({
      body: {
        finfoNumbers: itemCodes.finfoNumbers,
        gtinCodes: itemCodes.gtinCodes,
        mgItemNumbers: itemCodes.mgItemNumbers,
      },
    });
  };

  return {
    handleCreateMonitoringAssortment,
    handleDeleteMgAssortment,
    addItemsToAssortment,
    deleteItemsFromAssortment,
    handlePatchTitleOrItemsOfMgAssortment,
    handleValidateItemsOnImportedAssortment,
    isLoading:
      isLoadingPost ||
      isLoadingCreate ||
      isLoadingDeleteItems ||
      isLoadingDelete ||
      isLoadingChangeTitle,
  };
}
